import React, { useState } from 'react';
import { Checkbox, Flex, Radio } from '@mantine/core';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FileData } from 'src/data-capture/hooks/file-uploads/types';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { SelectableListRow } from './SelectableListRow';

type SelectableItem<S extends string | number> = {
  id: S;
  title: string;
  file: FileData;
  description?: string;
  right?: string;
  emptyView?: React.ReactNode;
  disabled?: boolean;
};

type Props<S extends string | number, T extends SelectableItem<S>> = {
  items: T[];
  hint?: string;
  selectedItemIds: S[];
  setSelectedItemIds: (itemIds: S[]) => void;
  selectMultiple?: boolean;
  emptyView?: React.ReactNode;
  highlightedElement?: string | undefined;
  showViewButton?: boolean;
  dateLayout?: 'inline' | 'stacked';
};

export function SuitabilitySelectableList<S extends string | number, T extends SelectableItem<S>>({
  items,
  selectedItemIds,
  setSelectedItemIds,
  selectMultiple,
  emptyView,
  highlightedElement,
  showViewButton = true,
  dateLayout = 'stacked',
}: Props<S, T>) {
  const Selector = selectMultiple ? Checkbox : Radio;

  const [hoveredId, setHoveredId] = useState<S | null>(null);
  const { createLink } = useFileTemporaryLinkMutation();

  const handleSelectItem = (item: T) => {
    if (item.disabled) return;

    const isSelected = selectedItemIds.includes(item.id);

    if (isSelected) {
      setSelectedItemIds(selectedItemIds.filter(id => id !== item.id));
      return;
    }

    if (selectMultiple) {
      setSelectedItemIds([...selectedItemIds, item.id]);
    } else {
      setSelectedItemIds([item.id]);
    }
  };

  const handleViewFile = async (uploadedFile: FileData) => {
    try {
      const link = await createLink(uploadedFile.id);
      window.open(link.url, '_blank');
    } catch (error) {
      notifyAndLogError(
        'Failed to open file',
        'An error occurred while trying to open the file. Please try again later.',
        error,
        { fileId: uploadedFile.id },
      );
    }
  };

  return (
    <Flex
      direction="column"
      gap="xxs"
      p="xs"
      bd="var(--solid-border)"
      bg="white"
      mih={0}
      style={{ borderRadius: 12 }}
    >
      {items.length === 0 && (emptyView ?? null)}

      {items.map(item => (
        <SelectableListRow<S, T>
          key={String(item.id)}
          item={item}
          isSelected={selectedItemIds.includes(item.id)}
          isHovered={hoveredId === item.id}
          setHoveredId={setHoveredId}
          onSelect={handleSelectItem}
          onViewFile={handleViewFile}
          selector={Selector}
          showViewButton={showViewButton}
          dateLayout={dateLayout}
          highlighted={highlightedElement != null && item.id === highlightedElement}
        />
      ))}
    </Flex>
  );
}
